import { Input, Flex, ActionButton, Button } from '@cycle-app/ui';
import { CloseIcon, LinkIcon, UndoIcon, RightIcon, CheckIcon } from '@cycle-app/ui/icons';
import { isUrl } from '@cycle-app/utilities';
import { useId, useState } from 'react';
import { Controller } from 'react-hook-form';

import { useChangelogBuilderFormContext } from 'src/hooks/releases/useChangelogBuilderFormContext';

import { PageId } from '../../constants/routing.constant';
import { useNavigate } from '../../hooks';
import { ToggleDropdown } from '../DropdownLayer';
import {
  Container, Label, Section, StyledButtonBack, StyledInput, Top,
} from './ChangelogBuilderSidebar.styles';

export const ChangelogBuilderSidebar = () => {
  const { navigate } = useNavigate();
  return (
    <Container>
      <Top>
        <StyledButtonBack
          iconStart={<RightIcon size={21} />}
          onClick={() => navigate(PageId.Releases)}
          size="L"
          variant="light"
        >
          Releases
        </StyledButtonBack>
      </Top>
      <div>
        <Titles />
        <Social />
      </div>
    </Container>
  );
};

const LabelWithReset = ({
  id, showReset, label, reset,
}: { label: string; id: string; showReset: boolean; reset: VoidFunction }) => {
  return (
    <Flex $align="center" $justify="space-between">
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <Label htmlFor={id}>{label}</Label>
      {showReset && (
        <ActionButton size="M" tooltip="Reset" tooltipPlacement="top" onClick={reset}>
          <UndoIcon size={14} />
        </ActionButton>
      )}
    </Flex>
  );
};

const Titles = () => {
  const {
    register, watch, formState, resetField,
  } = useChangelogBuilderFormContext();
  const id = useId();
  const title = watch('title');
  const subtitle = watch('subtitle');
  return (
    <Section>
      <Flex $column $align="stretch" $gap={20} style={{ marginTop: '20px' }}>
        <div>
          <LabelWithReset
            id={`${id}-title`}
            label="Title"
            reset={() => resetField('title')}
            showReset={title !== formState.defaultValues?.title}
          />
          <StyledInput
            error={formState.errors.title?.type === 'required' && 'Title is required'}
            id={`${id}-title`}
            {...register('title', { required: true })}
          />
        </div>
        <div>
          <LabelWithReset
            id={`${id}-subtitle`}
            label="Subtitle"
            reset={() => resetField('subtitle')}
            showReset={subtitle !== formState.defaultValues?.subtitle}
          />
          <StyledInput
            error={formState.errors.subtitle?.type === 'required' && 'Subtitle is required'}
            id={`${id}-subtitle`}
            {...register('subtitle', { required: true })}
          />
        </div>
      </Flex>
    </Section>
  );
};

const Social = () => {
  const {
    register, control, resetField, watch,
  } = useChangelogBuilderFormContext();
  const id = useId();
  const url = watch('socialLinkURL');
  return (
    <>
      <Section>
        <Input
          {...register('socialLinkText')}
          id={id}
          label="Social media"
          iconPadding={60}
          endInput={(
            <Flex>
              <ToggleDropdown
                content={({ hide }) => (
                  <Controller
                    control={control}
                    name="socialLinkURL"
                    render={({ field }) => <UpdateSocialUrlDialog initialValue={field.value} hide={hide} />}
                  />
                )}
                offsetY={20}
                placement="top"
                button={({
                  onClick, 'data-active': active,
                }) => (
                  <ActionButton
                    forceFocus={active}
                    size="L"
                    tooltpTitle={url ? 'Update social URL' : undefined}
                    tooltip={url || 'Update social URL'}
                    tooltipPlacement="top"
                    onClick={onClick}
                  >
                    <LinkIcon size={14} />
                  </ActionButton>
                )}
              />
              <ActionButton
                size="L"
                onClick={() => {
                  resetField('socialLinkText');
                  resetField('socialLinkURL');
                }}
                tooltip="Reset"
                tooltipPlacement="top"
              >
                <CloseIcon size={14} />
              </ActionButton>
            </Flex>
          )}
        />
      </Section>
    </>
  );
};

const UpdateSocialUrlDialog = ({
  initialValue,
  hide,
}: { initialValue: string; hide: VoidFunction }) => {
  const [url, setUrl] = useState(initialValue);
  const [error, setError] = useState('');
  const { setValue } = useChangelogBuilderFormContext();
  const id = useId();
  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        if (url) {
          if (isUrl(url)) {
            setValue('socialLinkURL', url);
            hide();
          } else {
            setError('You should provide a valid URL');
          }
        } else {
          // Not mandatory
          setValue('socialLinkURL', '');
          hide();
        }
      }}
    >
      <Flex
        $gap={8}
        $align="flex-start"
        style={{
          padding: '8px',
          width: '328px',
        }}
      >
        <div style={{ flex: '1' }}>
          <Input
            autoFocus
            error={error}
            id={id}
            onChange={e => {
              setError('');
              setUrl(e.currentTarget.value);
            }}
            placeholder="https://"
            value={url}
          />
        </div>
        <Button
          size="L"
          type="submit"
          variant="outlined-alt"
          style={{
            padding: 0,
            width: '40px',
            aspectRatio: '1',
            flex: 'none',
          }}
        >
          <CheckIcon size={24} />
        </Button>
      </Flex>
    </form>
  );
};
